/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import {
  CcfAccordion,
  CcfAccordionDetails,
  CcfAccordionSummary,
  CcfApprovedIcon,
  CcfCopilotSuggestionIcon,
  CcfDoubleArrowIcon,
  CcfTypography,
  CcfUpArrowIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  dispositionInteractionActions,
  getApplicationDirection,
  getDispositionData,
  getSpaceBtnCounterForCopilotVoice,
  getFinalSummary,
  getcopilotFlowMsgIndex,
} from '@nice-ccf/ui-components';
import styles from '../ccf-disposition.module.scss';
//import { interactionTab } from '../../ccf-interaction-workspace/ccf-interaction-space.slice';
import {
  DigitalChannelType,
  MediaType,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';
import dispositionAccordionStyles from '../ccf-disposition-accordion.styles';
import { customCopilotTemplate } from '../../ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/custom-flow';
import { customCopilotVoiceTemplate } from '../../ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/copilot-voice-custom-flow';

export interface CcfDispositionInteractionAccordionDetailsProps {
  allTabs: string[];
  clickedResolved?: boolean;
  contactId?: string;
  copilotAssist?: string;
  contactMedia?: string;
  type: string;
  validateFields?: any;
}

interface DispositionDataObjectType {
  Static: {
    priority: { value: string };
    status: { value: string };
    type: { value: string };
  };
  [MediaType.VOICE]?: {
    markcontactas: { value: string };
    tags: { value: string };
    notes: { value: string };
  };
  [MediaType.VIDEOCALL]?: {
    markcontactas: { value: string };
    tags: { value: string };
    notes: { value: string };
  };
  [MediaType.VOICEMAIL]?: {
    markcontactas: { value: string };
    tags: { value: string };
    notes: { value: string };
  };
  [MediaType.DIGITAL]?: {
    all: {
      value: string;
    };
    sms?: {
      value: string;
    };
    Email?: {
      value: string;
    };
    X?: {
      value: string;
    };
    Facebook?: {
      value: string;
    };
    Linkedin?: {
      value: string;
    };
    Instagram?: {
      value: string;
    };
    Chat?: {
      value: string;
    };
  };
}

/**
 * Component displays accordion details for disposition
 * @param props -CcfDispositionInteractionAccordionDetailsProps
 * @returns displays accordion details for disposition
 * @example <CcfDispositionInteractionAccordionDetails/>
 */
export function CcfDispositionInteractionAccordionDetails(
  props: CcfDispositionInteractionAccordionDetailsProps
) {
  const activeContact = useSelector(activeContactSelector);
  const theme = useTheme();
  const stylesAccordion = dispositionAccordionStyles(theme);
  let copilotFlowJson =
    activeContact?.customerChatReplyJsonData?.length > 0
      ? activeContact?.customerChatReplyJsonData
      : activeContact?.media === 'Voice' ?  customCopilotVoiceTemplate?.copilotAgentAndCustomerVoiceData : customCopilotTemplate?.copilotAgentAndCustomerChatData ;
  copilotFlowJson = copilotFlowJson.filter(
    (item: any) => item.messageFrom !== 'elevatedEmail'
  );
  copilotFlowJson = copilotFlowJson.filter(
    (item: any) => item.messageFrom !== 'elevatedSMS'
  );
  const [translate] = useTranslator();
  const accordionTextObj: any = {
    media: {
      [MediaType.DIGITAL]: {
        caption: `${[
          activeContact?.customer?.firstName,
          activeContact?.customer?.middleName,
          activeContact?.customer?.lastName,
        ]
          .filter(Boolean)
          .join(' ')}` as string,
        subText: translate('digital'),
        subTypes: {
          sms: {
            caption: translate('SMS'),
          },
          Email: {
            caption: translate('Email'),
          },
          X: {
            caption: translate('X'),
          },
          Facebook: {
            caption: translate('Facebook'),
          },
          Linkedin: {
            caption: translate('Linkedin'),
          },
          Instagram: {
            caption: translate('Instagram'),
          },
          Chat: {
            caption: translate('Chat'),
          },
        },
      },
      [MediaType.VOICE]: {
        caption:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? (`${[
                activeContact?.customer?.firstName,
                activeContact?.customer?.middleName,
                activeContact?.customer?.lastName,
              ]
                .filter(Boolean)
                .join(' ')}` as string)
            : translate('InboundVoice'),
        subText:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? translate('digital')
            : (activeContact?.customer.phoneNo as string),
      },
      [MediaType.VIDEOCALL]: {
        caption:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? (`${[
                activeContact?.customer?.firstName,
                activeContact?.customer?.middleName,
                activeContact?.customer?.lastName,
              ]
                .filter(Boolean)
                .join(' ')}` as string)
            : translate('InboundVideoCall'),
        subText:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? translate('digital')
            : (activeContact?.customer.phoneNo as string),
      },
      [MediaType.VOICEMAIL]: {
        caption:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? (`${[
                activeContact?.customer?.firstName,
                activeContact?.customer?.middleName,
                activeContact?.customer?.lastName,
              ]
                .filter(Boolean)
                .join(' ')}` as string)
            : translate('InboundVoiceMail'),
        subText:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? translate('digital')
            : (activeContact?.customer.phoneNo as string),
      },
    },
    static: {
      caption: translate('CRM'),
      subText: translate('ZenDesk'),
    },
  };

  const applicationDirection = useSelector(getApplicationDirection);
  const [isExpanded, setIsExpanded] = useState(true);
  const finalSummary = useSelector(getFinalSummary);
  const [outcomes, setOutcomes] = useState(finalSummary);
  const dispatch = useDispatch();
  const flowMsgIndex = useSelector(getcopilotFlowMsgIndex);
  const copilotVoiceJsonData = activeContact?.customerChatReplyJsonData;
  const copilotVoiceAssistList =
    copilotVoiceJsonData?.length > 0
      ? copilotVoiceJsonData
      : customCopilotVoiceTemplate?.copilotAgentAndCustomerVoiceData;
  const spaceBtnClickedCountForVoice = useSelector(
    getSpaceBtnCounterForCopilotVoice
  );

  // const [showSpinner, setShowSpinner] = useState(
  //   (activeContact?.autoSummary !== 'Off' &&
  //     activeContact?.media === MediaType.DIGITAL &&
  //     activeContact?.channel === 'Chat' &&
  //     flowMsgIndex >= copilotAgentAndCustomerChatData.length - 1) ||
  //     (activeContact?.autoSummary !== 'Off' &&
  //       activeContact?.media === MediaType.VOICE)
  //     ? true
  //     : false
  // );
  const [showSpinner, setShowSpinner] = useState(
    (activeContact?.copilotAssist === 'Yes' || activeContact?.copilotAssist === 'No' && activeContact?.autoSummary !== 'Off') ? true : false
  );

  const notesDefaultValue =
    'The customer called to request new insurance cards for their daughter, mentioning their employment with the company. The agent confirmed the policy number and offered assistance with ordering the cards. The agent provided information about the delivery timeframe of 10 business days. They also informed the customer about eligibility for a covered annual physical. The issue was resolved successfully.';
  activeContact?.autoSummary !== 'Off' &&
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (
        activeContact?.copilotAssist === 'Yes' &&
        showSpinner
      ) {
        setTimeout(() => {
          setShowSpinner(false);
        }, 5000);
      }
    }, []);

  useEffect(() => {
    if (
      activeContact?.autoSummary !== 'Off' &&
      activeContact?.media === MediaType.VOICE &&
      activeContact?.contactStatus === VoiceContactStatus.DISCONNECTED
    ) {
      setTimeout(() => {
        setShowSpinner(false);
      }, 5000);
    }
  }, [activeContact?.contactStatus]);

  useEffect(() => {
    if (
      activeContact?.copilotSource === 'Live LLM' &&
      outcomes.summary !== ''
    ) {
      setTimeout(() => {
        setShowSpinner(false);
      }, 5000);
    }
  }, [finalSummary]);

  /***
   * @example getText()
   */
  const getText = (type: string) =>
    props.type === 'media' && activeContact
      ? accordionTextObj.media[activeContact.media][type]
      : accordionTextObj.static[type];
  const [toggleExpandAndCollaspe, setToggleExpandAndCollaspe] = useState(true);
  const { setDispositionData, setDispositionResolution } =
    dispositionInteractionActions;
  const dispositionData = useSelector(getDispositionData);

  useEffect(() => {
    const newObj: DispositionDataObjectType = {
      Static: {
        priority: { value: '' },
        status: { value: '' },
        type: { value: '' },
      },
    };

    if (props?.contactMedia === MediaType.VOICE) {
      newObj[MediaType.VOICE] = {
        markcontactas: { value: '' },
        tags: { value: '' },
        notes: { value: '' },
      };
    } else if (props?.contactMedia === MediaType.VIDEOCALL) {
      newObj[MediaType.VOICE] = {
        markcontactas: { value: '' },
        tags: { value: '' },
        notes: { value: '' },
      };
    } else if (props?.contactMedia === MediaType.VOICEMAIL) {
      newObj[MediaType.VOICE] = {
        markcontactas: { value: '' },
        tags: { value: '' },
        notes: { value: '' },
      };
    } else if (props?.contactMedia === MediaType.DIGITAL) {
      newObj[MediaType.DIGITAL] = props.allTabs.reduce((a: any, c) => {
        a[c] = { value: '' };
        return a;
      }, {});
    }

    if (!dispositionData[props.contactId as string]) {
      dispatch(
        setDispositionData({
          [props.contactId as string]: {
            isResolved: false,
            ...newObj,
          },
        })
      );
    }
  }, []);

  useEffect(() => {
    setIsExpanded(true);
  }, [props.clickedResolved]);

  /**
   * Function to handle change of dropdown
   * @param event - any
   * @param caption - string
   * @param selectRef - any
   * @example - handleChange
   */
  const handleChange = (event: any, tabType: string, field: string) => {
    const value = event.target.value;
    // if (value !== '') {
    //   caption === 'DESCRIPTION'
    //     ? event.target.classList.remove('customDropdownForError')
    //     : caption === 'OUTCOME'
    //     ? selectRef.current.children[0]?.children[0]?.classList.remove(
    //         'customDropdownForError'
    //       )
    //     : selectRef.current.children[2]?.children[0]?.classList.remove(
    //         'customDropdownForError'
    //       );
    // }
    dispatch(
      setDispositionData({
        ...dispositionData,
        [props.contactId as string]: {
          ...dispositionData[props.contactId as string],
          [tabType]: {
            ...dispositionData[props.contactId as string][tabType],
            [field]: { value },
            ...(tabType === MediaType.DIGITAL &&
              field === 'all' &&
              props.allTabs.reduce((a: any, tab: string) => {
                a[tab] = { value };
                return a;
              }, {})),
          },
        },
      })
    );
    const mediaType = activeContact?.media;
    props.validateFields(value, field, tabType, mediaType);
    dispatch(
      setDispositionResolution({
        contactId: props.contactId,
        isResolved: true,
      })
    );
  };

  /**
   * Function to handle accordion change
   * @example - handleAccordionChange
   */
  const handleAccordionChange = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * Function to handle exapnad and collapse of acccordion
   @example - handleExapndAndCollapse 
   */
  const handleExpandAndCollapse = () => {
    setToggleExpandAndCollaspe(!toggleExpandAndCollaspe);
  };

  /**
   * Function to handle input value for dropdown
   * @param field - string
   * @returns - input value for dropdown
   * @example - inputValue
   */
  const inputValue = (tabType: string, field: string) =>
    dispositionData[props.contactId as string] &&
    dispositionData[props.contactId as string][tabType]
      ? dispositionData[props.contactId as string][tabType][field]?.value
      : '';

  return (
    <CcfAccordion
      sx={stylesAccordion.accordionContainer}
      expanded={isExpanded}
      onChange={handleAccordionChange}
    >
      <CcfAccordionSummary
        expandIcon={<CcfDoubleArrowIcon sx={stylesAccordion.expandedIcon} />}
        aria-controls="detail-content"
        id="agent_disposition-header"
        sx={stylesAccordion.accordionHeader}
        onClick={handleExpandAndCollapse}
      >
        <div className="textForDispositionCards">
          <CcfTypography className="textForDispositionDetails">
            {getText('caption')}
          </CcfTypography>
          <CcfTypography className="subTextForDispositionDetails">
            {getText('subText')}
          </CcfTypography>
        </div>
      </CcfAccordionSummary>
      <CcfAccordionDetails sx={stylesAccordion.addressDetails}>
        {props.type === 'media' && (
          <>
            {(props.contactMedia === MediaType.VOICE ||
              props.contactMedia === MediaType.VIDEOCALL ||
              props.contactMedia === MediaType.VOICEMAIL ||
              (activeContact?.media === MediaType.DIGITAL &&
                activeContact?.channel === 'Chat' &&
                activeContact?.copilotAssist === 'Yes')) && (
              <>
                <CcfTypography sx={stylesAccordion.captionForDropdown}>
                  {translate('status')} <span style={{ color: 'red' }}>*</span>
                </CcfTypography>
                <Select
                  id={`agent_outcomes_status_dropdown`}
                  sx={stylesAccordion.customDropdown}
                  value={inputValue(MediaType.VOICE, 'disposition')}
                  onChange={(e) =>
                    handleChange(e, MediaType.VOICE, 'disposition')
                  }
                  disableUnderline
                  IconComponent={() => (
                    <CcfUpArrowIcon className="arrowIconForDropdown" />
                  )}
                  displayEmpty
                >
                  <MenuItem
                    classes={{
                      root: styles.dropdownOption,
                    }}
                    value={undefined}
                    hidden
                    disabled
                    id="agent_outcomes_status_selectItem"
                  >
                    <span className="placeholderText">
                      {translate('selectItem')}
                    </span>
                  </MenuItem>
                  <MenuItem
                    classes={{
                      root: styles.dropdownOption,
                    }}
                    id="agent_outcomes_status_resolved"
                    value={'MarkAsResolved'}
                  >
                    {translate('resolved')}
                  </MenuItem>
                  <MenuItem
                    classes={{
                      root: styles.dropdownOption,
                    }}
                    id="agent_outcomes_status_pending"
                    value={'Pending'}
                  >
                    {translate('pending')}
                  </MenuItem>
                </Select>
                <CcfTypography sx={stylesAccordion.captionForDropdown}>
                  {translate('disposition')}{' '}                  
                </CcfTypography>
                <Select
                  id={`agent_outcomes_disposition_dropdown`}
                  sx={stylesAccordion.customDropdown}
                  value={inputValue(MediaType.VOICE, 'tags')}
                  onChange={(e) => handleChange(e, MediaType.VOICE, 'tags')}
                  disableUnderline
                  IconComponent={() => (
                    <CcfUpArrowIcon className="arrowIconForDropdown" />
                  )}
                  displayEmpty
                >
                  <MenuItem
                    classes={{
                      root: styles.dropdownOption,
                    }}
                    id="agent_outcomes_disposition_Select item"
                    value={undefined}
                    hidden
                    disabled
                  >
                    <span className="placeholderText">
                      {translate('selectItem')}
                    </span>
                  </MenuItem>
                  {activeContact?.copilotSource !== 'Live LLM' && (
                    <MenuItem
                      classes={{
                        root: styles.dropdownOption,
                      }}
                      id="agent_outcomes_disposition_Travel_Reservations"
                      value={translate('travelReservation')}
                    >
                      {translate('travelReservation')}
                    </MenuItem>
                  )}
                  <MenuItem
                    classes={{
                      root: styles.dropdownOption,
                    }}
                    id="agent_outcomes_disposition_Yes"
                    value={'Yes'}
                  >
                    {translate('yes')}
                  </MenuItem>
                  <MenuItem
                    classes={{
                      root: styles.dropdownOption,
                    }}
                    id="agent_outcomes_disposition_No"
                    value={'No'}
                  >
                    {translate('no')}
                  </MenuItem>
                </Select>

                {activeContact?.media !== MediaType.VIDEOCALL &&
                  activeContact?.media !== MediaType.VOICEMAIL && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          marginTop: '5px',
                        }}
                      >
                        <CcfTypography
                          sx={stylesAccordion.captionForDropdown}
                          translationKey="notes"
                        ></CcfTypography>
                        {((activeContact?.autoSummary !== 'Off' &&
                          !showSpinner &&
                          flowMsgIndex >= copilotFlowJson.length - 1) ||
                          (activeContact?.autoSummary !== 'Off' &&
                            !showSpinner &&
                            spaceBtnClickedCountForVoice ===
                              copilotVoiceAssistList.length)) && (
                          <div className="autoSummaryChip">
                            <CcfApprovedIcon className="approvedIcon" />
                            <CcfTypography className="txtautoSummaryCompleted">
                              {translate('autosummaryCompleted')}
                            </CcfTypography>
                          </div>
                        )}
                      </div>

                      <div
                        className={
                          showSpinner
                            ? 'disposition__textarea_for_autosummary'
                            : ''
                        }
                      >
                        {' '}
                        {activeContact?.autoSummary !== 'Off' && showSpinner ? (
                          <Box mt={3} width="100%" className="spinner-block">
                            <CircularProgress
                              size={20}
                              sx={{
                                '& .MuiCircularProgress-circle': {
                                  color: '#000000',
                                },
                              }}
                              variant="indeterminate"
                            />
                            <CcfCopilotSuggestionIcon className="copilotIcon" />
                            <CcfTypography
                              className="spinner_text"
                              id="agent_outcomes_spinner"
                            >
                              {' '}
                              {translate('generatingSummary') + '...'}
                              <br />
                              {translate('summaryGenerationMsg')}
                            </CcfTypography>
                          </Box>
                        ) : (
                          <textarea
                            className="disposition__textarea"
                            placeholder="Enter Notes"
                            id="agent_outcomes_autoSummary"
                            onChange={(e) =>
                              handleChange(e, MediaType.VOICE, 'notes')
                            }
                            style={{
                              height:
                                activeContact?.autoSummary !== 'Off'
                                  ? '190px'
                                  : '80px',
                            }}
                            defaultValue={
                              activeContact?.copilotSource === 'Live LLM'
                              ?  outcomes.summary
                              : showSpinner
                              ? ''
                              : activeContact?.copilotAssist === 'Yes'
                              ? activeContact.autoSummaryText
                              : (activeContact?.autoSummary === 'Custom' || activeContact?.autoSummary === 'On')
                              ? activeContact.autoSummaryText
                              : inputValue(MediaType.VOICE, 'notes')
                            }
                            // value={finalSummary}
                          ></textarea>
                        )}
                      </div>
                    </>
                  )}
              </>
            )}
            {(((activeContact?.media === MediaType.DIGITAL ||
              props.contactMedia === MediaType.DIGITAL) &&
              activeContact?.channel !== 'Chat') ||
              (activeContact?.channel === 'Chat' &&
                activeContact?.copilotAssist === 'No')) && (
              <>
                {props.allTabs && props.allTabs.length > 1 && (
                  <>
                    <CcfTypography
                      sx={stylesAccordion.captionForDropdown}
                      translationKey="markAllAs"
                    ></CcfTypography>
                    <Select
                      id={`agent_outcomes_markAllAs_${inputValue(
                        MediaType.DIGITAL,
                        'all'
                      )}`}
                      sx={stylesAccordion.customDropdown}
                      value={inputValue(MediaType.DIGITAL, 'all')}
                      onChange={(e) =>
                        handleChange(e, MediaType.DIGITAL, 'all')
                      }
                      disableUnderline
                      IconComponent={() => (
                        <CcfUpArrowIcon className="arrowIconForDropdown" />
                      )}
                      displayEmpty
                    >
                      <MenuItem
                        classes={{
                          root: styles.dropdownOption,
                        }}
                        id="agent_outcomes_markAllAs_Select item"
                        value={props.allTabs.length > 1 ? '' : undefined}
                        hidden
                        disabled
                      >
                        <span className="placeholderText">
                          {translate('selectItem')}
                        </span>
                      </MenuItem>
                      <MenuItem
                        classes={{
                          root: styles.dropdownOption,
                        }}
                        id="agent_outcomes_markAllAs_Resolved"
                        value={'Resolved'}
                      >
                        {translate('resolved')}
                      </MenuItem>
                      <MenuItem
                        classes={{
                          root: styles.dropdownOption,
                        }}
                        id="agent_outcomes_markAllAs_Pending"
                        value={'Pending'}
                      >
                        {translate('pending')}
                      </MenuItem>
                    </Select>
                    {props.allTabs.map((tab, index) => (
                      <div className="dynamicTabsDropDown">
                        <CcfTypography sx={stylesAccordion.captionForDropdown}>
                          {`${
                            accordionTextObj.media[MediaType.DIGITAL].subTypes[
                              tab
                            ].caption
                          } as`}
                        </CcfTypography>
                        <Select
                          id={`agent_outcomes_markAllAs_${tab}`}
                          sx={stylesAccordion.customDropdown}
                          value={inputValue(MediaType.DIGITAL, tab)}
                          onChange={(e) =>
                            handleChange(e, MediaType.DIGITAL, tab)
                          }
                          disableUnderline
                          IconComponent={() => (
                            <CcfUpArrowIcon className="arrowIconForDropdown" />
                          )}
                          displayEmpty
                        >
                          <MenuItem
                            classes={{
                              root: styles.dropdownOption,
                            }}
                            value={index === 0 ? '' : undefined}
                            hidden
                            disabled
                          >
                            <span className="placeholderText">
                              {translate('selectItem')}
                            </span>
                          </MenuItem>
                          <MenuItem
                            classes={{
                              root: styles.dropdownOption,
                            }}
                            value={'Resolved'}
                          >
                            {translate('resolved')}
                          </MenuItem>
                          <MenuItem
                            classes={{
                              root: styles.dropdownOption,
                            }}
                            value={'Pending'}
                          >
                            {translate('pending')}
                          </MenuItem>
                        </Select>
                      </div>
                    ))}
                  </>
                )}
                {props.allTabs.length === 1 && (
                  <>
                    <CcfTypography sx={stylesAccordion.captionForDropdown}>
                      {`${'mark'} ${
                        accordionTextObj.media[MediaType.DIGITAL].subTypes[
                          props.allTabs[0]
                        ].caption
                      } ${'as'}`}
                    </CcfTypography>
                    <Select
                      id={`agent_outcomes_markAllAs_${props.allTabs[0]}`}
                      sx={stylesAccordion.customDropdown}
                      value={inputValue(MediaType.DIGITAL, props.allTabs[0])}
                      onChange={(e) =>
                        handleChange(e, MediaType.DIGITAL, props.allTabs![0])
                      }
                      disableUnderline
                      IconComponent={() => (
                        <CcfUpArrowIcon className="arrowIconForDropdown" />
                      )}
                      displayEmpty
                    >
                      <MenuItem
                        classes={{
                          root: styles.dropdownOption,
                        }}
                        value={undefined}
                        hidden
                        disabled
                      >
                        <span className="placeholderText">
                          {translate('selectItem')}
                        </span>
                      </MenuItem>
                      <MenuItem
                        classes={{
                          root: styles.dropdownOption,
                        }}
                        id="agent_outcomes_status_resolved"
                        value={'MarkAsResolved'}
                      >
                        {translate('resolved')}
                      </MenuItem>
                      <MenuItem
                        classes={{
                          root: styles.dropdownOption,
                        }}
                        value={'Pending'}
                        id="agent_outcomes_status_pending"
                      >
                        {translate('pending')}
                      </MenuItem>
                    </Select>
                  </>
                )}
              </>
            )}
          </>
        )}

        {props.type === 'static' && (
          <>
            <CcfTypography sx={stylesAccordion.captionForDropdown}>
              Priority
            </CcfTypography>
            <Select
              id="priority"
              sx={stylesAccordion.customDropdown}
              value={inputValue('Static', 'priority')}
              onChange={(e) => handleChange(e, 'Static', 'priority')}
              disableUnderline
              IconComponent={() => (
                <CcfUpArrowIcon className="arrowIconForDropdown" />
              )}
              displayEmpty
            >
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                value=""
                hidden
                disabled
              >
                <span className="placeholderText">
                  {translate('selectItem')}
                </span>
              </MenuItem>
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                value={'High'}
              >
                High
              </MenuItem>
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                value={'Medium'}
              >
                Medium
              </MenuItem>
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                value={'Low'}
              >
                Low
              </MenuItem>
            </Select>
            <CcfTypography sx={stylesAccordion.captionForDropdown}>
              Status
            </CcfTypography>
            <Select
              id="status"
              sx={stylesAccordion.customDropdown}
              value={inputValue('Static', 'status')}
              onChange={(e) => handleChange(e, 'Static', 'status')}
              disableUnderline
              IconComponent={() => (
                <CcfUpArrowIcon className="arrowIconForDropdown" />
              )}
              displayEmpty
            >
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                value=""
                hidden
                disabled
              >
                <span className="placeholderText">
                  {translate('selectItem')}
                </span>
              </MenuItem>
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                id="agent_outcomes_status_resolved"
                value={'MarkAsResolved'}
              >
                {translate('resolved')}
              </MenuItem>
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                id="agent_outcomes_status_pending"
                value={'Pending'}
              >
                {translate('pending')}
              </MenuItem>
            </Select>
            <CcfTypography sx={stylesAccordion.captionForDropdown}>
              Type
            </CcfTypography>
            <Select
              id="type"
              sx={stylesAccordion.customDropdown}
              value={inputValue('Static', 'type')}
              onChange={(e) => handleChange(e, 'Static', 'type')}
              disableUnderline
              IconComponent={() => (
                <CcfUpArrowIcon className="arrowIconForDropdown" />
              )}
              displayEmpty
            >
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                value={undefined}
                hidden
                disabled
              >
                <span className="placeholderText">
                  {translate('selectItem')}
                </span>
              </MenuItem>
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                value={'Yes'}
              >
                {translate('yes')}
              </MenuItem>
              <MenuItem
                classes={{
                  root: styles.dropdownOption,
                }}
                value={'No'}
              >
                {translate('no')}
              </MenuItem>
            </Select>
          </>
        )}
      </CcfAccordionDetails>
    </CcfAccordion>
  );
}

export default CcfDispositionInteractionAccordionDetails;
